import { useState, useCallback, useEffect, useRef } from 'react';
import { getFeedsCommentsV2 } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import type { IDiscussionItem } from '../components/HotEventPanel/types';

export type TContentTab = 'Posts' | 'Comments';

const POSTS_REVIEW_TYPES = ['posts_text', 'posts_video', 'stream_titles'];
const COMMENTS_REVIEW_TYPES = [
  'sub_text',
  'sub_video',
  'comments_on_streams',
  'live_chats_on_streams',
];

const TAB_REVIEW_TYPES: Record<TContentTab, string[]> = {
  Posts: POSTS_REVIEW_TYPES,
  Comments: COMMENTS_REVIEW_TYPES,
};

const PAGE_SIZE = 20;

/**
 * Hot Event 主贴/评论分页 Hook
 * 使用 /feeds/comments 接口，支持滚动分页加载
 *
 * 注意：切换 Tab 时 TabPanel 卸载未选中内容，Hook 随组件销毁，
 * 重新挂载时以新的 tabType 自动发起请求。item 变化时重新请求。
 */
export const useHotEventFeedsComments = (
  item: IDiscussionItem | null,
  gameInfo: any,
  filterParams: Record<string, any> | undefined,
  tabType: TContentTab,
  channelType: string,
  sortBy?: string,
  languageFilter?: string[],
) => {
  const [items, setItems] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);

  // 记录上一次请求标识，避免父组件重复渲染时重复请求
  const lastFetchKeyRef = useRef<string | null>(null);

  const { loading, wrapperCancelRequest } = useCancelRequestHook({
    defaultLoading: true,
  });

  // 真实 API 请求（依赖 item/gameInfo/filterParams/tabType/sortBy 用于构造请求参数）
  const fetchData = useCallback(
    async (pageNum: number, isAppend: boolean) => {
      if (!item || !gameInfo?.edition_unified_id) return;

      if (isAppend) {
        setLoadingMore(true);
      }

      try {
        const data = await wrapperCancelRequest(getFeedsCommentsV2, {
          edition_unified_id: gameInfo.edition_unified_id,
          id_type: gameInfo.id_type || 'unified_id',
          entity_type: gameInfo.entity_type || 'mobile',
          // channel_type: channelType,
          page_source: 'hot_event',
          source_id: item.cluster_id,
          event_start_time: item.start_time,
          event_end_time: item.end_time,
          start_time: item.start_time,
          end_time: item.end_time,
          page_size: PAGE_SIZE,
          sort_item: sortBy,
          sort_model: 'desc',
          review_types: TAB_REVIEW_TYPES[tabType],
          page: pageNum,
          language: languageFilter,
        });

        if (!data) return;

        const list: any[] = (data as any)?.list || [];

        if (isAppend) {
          setItems(prev => [...prev, ...list]);
        } else {
          setItems(list);
        }

        setHasMore(list.length >= PAGE_SIZE);
      } catch {
        // wrapperCancelRequest 已处理错误提示
      } finally {
        setLoadingMore(false);
      }
    },
    [item, filterParams, tabType, channelType, sortBy, languageFilter],
  );

  // 首次挂载 / item 变化 / tabType 变化 / sortBy 变化 → 重置并重新请求
  // currentKey 包含 sortBy，保证排序切换时也能正确触发重新请求
  // 使用 lastFetchKeyRef 避免父组件多次渲染导致引用变化而重复请求
  useEffect(() => {
    if (!item || !gameInfo?.edition_unified_id) return;

    const currentKey = `${item.cluster_id}_${tabType}_${sortBy}_${languageFilter?.join(',') || ''}`;
    if (lastFetchKeyRef.current === currentKey) return;

    lastFetchKeyRef.current = currentKey;
    pageRef.current = 1;
    setHasMore(true);
    setItems([]);
    fetchData(1, false);
  }, [item, gameInfo, tabType, sortBy, languageFilter, fetchData]);

  // 滚动触底 → 加载下一页
  const handleScrollBottom = useCallback(() => {
    if (!hasMore || loadingMore || loading) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    fetchData(nextPage, true);
  }, [hasMore, loadingMore, loading, fetchData]);

  return { items, loading, loadingMore, hasMore, handleScrollBottom };
};
